function stripLeadingWww(host: string): string {
  return host.replace(/^www\./i, "");
}

function readCanonicalOriginFromEnv(): string {
  const raw = (process.env.NEXT_PUBLIC_SITE_URL || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw);
    url.hostname = stripLeadingWww(url.hostname);
    return url.origin;
  } catch {
    return "";
  }
}

function shouldUseCurrentOriginForCanonical(host: string): boolean {
  return (
    host === "localhost" ||
    host.endsWith(".local") ||
    host.endsWith(".trycloudflare.com") ||
    host === "dev.hireaera.com"
  );
}

/** Real app origin for LINE/LIFF navigation and www→apex alignment. */
export function getCanonicalOrigin(): string {
  if (typeof window !== "undefined") {
    const host = window.location.hostname || "";
    if (shouldUseCurrentOriginForCanonical(host)) {
      return window.location.origin;
    }
    const fromEnv = readCanonicalOriginFromEnv();
    if (fromEnv) return fromEnv;
    return window.location.origin;
  }
  return readCanonicalOriginFromEnv();
}

/**
 * Production: `www.hireaera.com` → `hireaera.com` before auth/routing (localStorage is per-origin).
 * Passes `auth_token` once via query so apex can hydrate.
 */
const SIGNIN_CANONICAL_REDIRECT_ONCE_KEY = "signin_canonical_redirect_once_v1";

export function redirectToCanonicalAppOriginIfNeeded(): boolean {
  if (typeof window === "undefined") return false;

  const host = window.location.hostname || "";
  if (shouldUseCurrentOriginForCanonical(host)) return false;

  const canonical = getCanonicalOrigin();
  if (!canonical) return false;
  try {
    const canonicalHost = stripLeadingWww(new URL(canonical).hostname);
    const currentHost = stripLeadingWww(window.location.hostname || "");
    if (canonicalHost === currentHost) return false;
  } catch {
    if (window.location.origin === canonical) return false;
  }

  try {
    if (sessionStorage.getItem(SIGNIN_CANONICAL_REDIRECT_ONCE_KEY) === "1") {
      return false;
    }
    sessionStorage.setItem(SIGNIN_CANONICAL_REDIRECT_ONCE_KEY, "1");
  } catch {
    return false;
  }

  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const target = new URL(path, canonical);

  try {
    const token = localStorage.getItem("auth_token")?.trim();
    if (token && !target.searchParams.has("token")) {
      target.searchParams.set("token", token);
      target.searchParams.set("from", "canonical_origin");
    }
  } catch {
    // ignore
  }

  window.location.replace(target.toString());
  return true;
}
